<template>
  <div class="modal-overlay" @click.self="handleClose">
    <div class="modal-container">
      <div class="modal-header">
        <button
          type="button"
          class="modal-close-button"
          @click="handleClose"
          aria-label="Close launch progress"
        >
          ×
        </button>
        <h3>{{ mainHeading }}</h3>
      </div>

      <div class="modal-body">
        <div class="header-section">
          <div class="spinner-wrap">
            <div class="spinner-container" :class="{ completed: allStatesComplete }">
              <div class="spinner-ring"></div>
              <div class="spinner-core"></div>
              <div v-if="allStatesComplete" class="spinner-badge" aria-hidden="true">✓</div>
            </div>
          </div>
          <p v-if="!allStatesComplete" class="loading-word">{{ currentLoadingMessage }}</p>
          <p class="main-message">{{ mainMessage }}</p>
        </div>

        <div class="states-container">
          <div
            v-for="(state, index) in displayStates"
            :key="state.name"
            class="state-item"
            :class="{
              pending: state.displayStatus === 'pending',
              running: state.displayStatus === 'running',
              completed: state.displayStatus === 'completed',
              failed: state.displayStatus === 'failed'
            }"
          >
            <div class="state-indicator">
              <div v-if="state.displayStatus === 'completed'" class="check-icon">✓</div>
              <div v-else-if="state.displayStatus === 'failed'" class="error-icon">✗</div>
              <div v-else-if="state.displayStatus === 'running'" class="running-spinner"></div>
              <div v-else class="pending-dot"></div>
            </div>

            <div class="state-content">
              <div class="state-name">{{ state.friendlyName }}</div>
              <div class="state-description">{{ state.description }}</div>
            </div>

            <div v-if="index < displayStates.length - 1" class="state-connector"></div>
          </div>
        </div>

        <div v-if="error" class="error-message">
          <span class="error-icon-inline">⚠️</span>
          <span>{{ error }}</span>
        </div>
      </div>

      <div v-if="allStatesComplete" class="modal-footer">
        <button
          type="button"
          class="cta-button cta-button--primary"
          :disabled="!canViewLive"
          @click="handleViewLive"
          aria-label="Open the live campaign in a new tab"
        >
          View campaign
        </button>
        <button
          type="button"
          class="cta-button"
          @click="handleOpenBilling"
          aria-label="Go to billing"
        >
          Billing
        </button>
        <button
          type="button"
          class="cta-button"
          @click="handleContinue"
          aria-label="Go to campaign dashboard"
        >
          Dashboard
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { ApiService } from '@/services/api.service'
import { useCampaignBuilderStore } from '@/store/campaignBuilder.store'

export interface StateInfo {
  name: string
  friendlyName: string
  description: string
  actualStatus: 'pending' | 'running' | 'completed' | 'failed'
  displayStatus: 'pending' | 'running' | 'completed' | 'failed'
  completedAt?: Date
}

export default defineComponent({
  name: 'LaunchModal',
  props: {
    submissionId: {
      type: String,
      required: true
    }
  },
  emits: ['close', 'complete', 'reset'],
  setup(props, { emit }) {
    const router = useRouter()
    const campaignStore = useCampaignBuilderStore()

    const loadingMessages = [
      'percolating',
      'musing',
      'conjuring',
      'brewing',
      'crafting',
      'weaving',
      'orchestrating',
      'manifesting'
    ]

    const currentLoadingMessage = ref(loadingMessages[0])
    let messageRotationInterval: number | null = null

    // Backend state-machine steps. When the campaign includes a MindAR
    // marker we prepend an extra "marker" step derived from the client-side
    // compile status — by the time this modal opens the compile is already
    // done (Phase 1 runs it synchronously in submit), so it shows as
    // completed, but keeping it in the list gives the user confirmation
    // that all five stages ran.
    const hasMarkerStep = computed(() =>
      Boolean(campaignStore.markerTarget.mindFileUrl),
    )

    const backendStates = ref<StateInfo[]>([
      {
        name: 'GetBusinessTemplate',
        friendlyName: 'Initializing System',
        description: 'Waking up our engine and getting things started...',
        actualStatus: 'pending',
        displayStatus: 'pending'
      },
      {
        name: 'GetDuplicateTemplate',
        friendlyName: 'Forging Campaign',
        description: 'Crafting a unique instance just for you...',
        actualStatus: 'pending',
        displayStatus: 'pending'
      },
      {
        name: 'GetCustomiseTemplate',
        friendlyName: 'Infusing Magic',
        description: 'Applying your customisations and brand identity...',
        actualStatus: 'pending',
        displayStatus: 'pending'
      },
      {
        name: 'MakeCampaignCommunications',
        friendlyName: 'Dispatching Messenger',
        description: 'Preparing notifications and final touches...',
        actualStatus: 'pending',
        displayStatus: 'pending'
      }
    ])

    const markerStepStatus = computed<'completed' | 'failed' | 'pending'>(() => {
      const status = campaignStore.markerTarget.compileStatus
      if (status === 'error') return 'failed'
      if (campaignStore.markerTarget.mindFileUrl) return 'completed'
      return 'pending'
    })

    const displayStates = computed<StateInfo[]>(() => {
      if (!hasMarkerStep.value) return backendStates.value
      return [
        {
          name: 'CompileMarkerTarget',
          friendlyName: 'Compiled Marker Target',
          description: 'Your image was validated and turned into an AR marker.',
          actualStatus: markerStepStatus.value,
          displayStatus: markerStepStatus.value,
        },
        ...backendStates.value,
      ]
    })

    const error = ref<string | null>(null)
    const pollingInterval = ref<number | null>(null)
    const queueProcessorInterval = ref<number | null>(null)
    const pollingStartTime = ref<number>(0)
    const delayQueue = ref<Array<{ stateIndex: number, newStatus: 'running' | 'completed', timestamp: Date }>>([])
    // The tracking `submissionId` we were handed by /manager/campaigns is
    // NOT the id of the created campaign — the state machine's duplicate
    // step generates a new campaign id (stored against the submission in
    // DynamoDB). The poll endpoint surfaces it as `campaignId` once the
    // state machine has run. Until that resolves we can't build a valid
    // live-campaign URL, so the View Campaign button stays disabled.
    const resolvedCampaignId = ref<string | null>(null)

    const allStatesComplete = computed(() =>
      displayStates.value.every(s => s.displayStatus === 'completed')
    )

    const mainHeading = computed(() => {
      if (error.value) return 'Oops! Something Went Wrong'
      if (allStatesComplete.value) return 'Campaign Ready!'
      return 'Building Your Campaign'
    })

    const mainMessage = computed(() => {
      if (error.value) return 'We encountered an issue while building your campaign.'
      if (allStatesComplete.value) {
        return 'Your campaign is ready. We sent you an email — go play the experience!'
      }
      return 'We are setting up your campaign. This usually takes just a moment...'
    })

    // Rotate loading messages
    const startMessageRotation = () => {
      let index = 0
      messageRotationInterval = window.setInterval(() => {
        index = (index + 1) % loadingMessages.length
        currentLoadingMessage.value = loadingMessages[index]
      }, 2000) // Change message every 2 seconds
    }

    const stopMessageRotation = () => {
      if (messageRotationInterval !== null) {
        clearInterval(messageRotationInterval)
        messageRotationInterval = null
      }
    }

    // Process delay queue to show state changes with 3-second delays
    const processDelayQueue = () => {
      const now = new Date()
      const readyItems = delayQueue.value.filter(item => {
        const elapsed = now.getTime() - item.timestamp.getTime()
        return elapsed >= 3000 // 3 seconds
      })

      readyItems.forEach(item => {
        if (item.stateIndex < backendStates.value.length) {
          backendStates.value[item.stateIndex].displayStatus = item.newStatus

          // Remove from queue
          const queueIndex = delayQueue.value.indexOf(item)
          if (queueIndex > -1) {
            delayQueue.value.splice(queueIndex, 1)
          }
        }
      })
    }

    // Add state change to delay queue
    const queueStateChange = (stateIndex: number, newStatus: 'running' | 'completed') => {
      // Check if there's already a queued or displayed change for this state at this status
      const alreadyQueued = delayQueue.value.some(
        item => item.stateIndex === stateIndex && item.newStatus === newStatus
      )
      const alreadyDisplayed = backendStates.value[stateIndex].displayStatus === newStatus

      if (!alreadyQueued && !alreadyDisplayed) {
        delayQueue.value.push({
          stateIndex,
          newStatus,
          timestamp: new Date()
        })
      }
    }

    // Poll state machine status
    const pollStatus = async () => {
      try {
        const result = await ApiService.getCampaignStateMachineStatus(props.submissionId)

        console.log('State machine poll result:', result)

        if (result.campaignId && !resolvedCampaignId.value) {
          resolvedCampaignId.value = result.campaignId
        }

        const failedStateFromApi = result.states.find(state => state.status === 'failed')
        const failureDetail =
          result.errorMessage ||
          failedStateFromApi?.errorMessage ||
          result.error ||
          result.failureCause ||
          null
        const executionFailed =
          result.status === 'FAILED' ||
          result.status === 'TIMED_OUT' ||
          result.status === 'ABORTED'

        // Update actual state statuses
        result.states.forEach((apiState, index) => {
          if (index < backendStates.value.length) {
            const currentState = backendStates.value[index]
            currentState.actualStatus = apiState.status

            if (apiState.status === 'running' && currentState.displayStatus === 'pending') {
              queueStateChange(index, 'running')
            } else if (apiState.status === 'completed' && currentState.displayStatus !== 'completed') {
              if (currentState.displayStatus === 'pending') {
                queueStateChange(index, 'running')
              }
              queueStateChange(index, 'completed')
            } else if (apiState.status === 'failed') {
              currentState.displayStatus = 'failed'
            }
          }
        })

        if (executionFailed) {
          const failedState = failedStateFromApi
            ? backendStates.value[result.states.findIndex(state => state.name === failedStateFromApi.name)]
            : null
          const friendlyName = failedState?.friendlyName ?? failedStateFromApi?.name ?? null
          const messagePrefix = friendlyName ? `${friendlyName} failed` : 'Campaign creation failed'
          error.value = failureDetail ? `${messagePrefix}: ${failureDetail}` : `${messagePrefix}. Please try again or contact support.`
          stopPolling()
          stopMessageRotation()
          return
        }

        // Check if all states are actually completed
        if (result.status === 'SUCCEEDED') {
          backendStates.value.forEach((state, index) => {
            if (state.displayStatus === 'pending') {
              state.displayStatus = 'running'
              setTimeout(() => {
                if (backendStates.value[index]) {
                  backendStates.value[index].displayStatus = 'completed'
                }
              }, 500)
            } else if (state.displayStatus !== 'completed') {
              state.displayStatus = 'completed'
            }
          })

          stopPolling()
          stopMessageRotation()
        }

      } catch (err) {
        console.error('Error polling state machine status:', err)
        if (pollingInterval.value !== null && Date.now() - pollingStartTime.value > 10000) {
          error.value = 'Unable to check campaign status. Please refresh the page.'
        }
      }
    }

    const startPolling = () => {
      // Poll immediately
      pollStatus()

      // Track when polling started
      pollingStartTime.value = Date.now()

      // Then poll every 2 seconds
      pollingInterval.value = window.setInterval(pollStatus, 2000)

      // Also process delay queue every 500ms
      queueProcessorInterval.value = window.setInterval(processDelayQueue, 500)
    }

    const stopPolling = () => {
      if (pollingInterval.value !== null) {
        clearInterval(pollingInterval.value)
        pollingInterval.value = null
      }
      if (queueProcessorInterval.value !== null) {
        clearInterval(queueProcessorInterval.value)
        queueProcessorInterval.value = null
      }
    }

    // Build the live campaign URL the same way the dashboard does so the
    // view button opens the exact URL the user will share. MUST use the
    // resolved campaignId (set by the status poll after the duplicate
    // step creates it), not the submissionId — those are different.
    const buildLiveCampaignUrl = (): string | null => {
      const id = resolvedCampaignId.value
      if (!id) return null
      const origin = typeof window !== 'undefined' ? window.location.origin : ''
      const liveAppOrigin = origin.includes('builder.')
        ? origin.replace('builder.', 'app.')
        : origin.includes('creator.innereality.com')
          ? 'https://app.prodc.innereality.com'
          : origin
      return `${liveAppOrigin}/${encodeURIComponent(id)}`
    }

    const canViewLive = computed(() => Boolean(resolvedCampaignId.value))

    const handleClose = () => {
      if (allStatesComplete.value || error.value) {
        emit('close')
      }
    }

    const handleContinue = () => {
      emit('complete')
      emit('close')
    }

    const handleViewLive = () => {
      const url = buildLiveCampaignUrl()
      if (!url || typeof window === 'undefined') return
      window.open(url, '_blank', 'noopener,noreferrer')
    }

    const handleOpenBilling = () => {
      emit('close')
      void router.push({ name: 'billing' })
    }

    onMounted(() => {
      startPolling()
      startMessageRotation()
    })

    onBeforeUnmount(() => {
      stopPolling()
      stopMessageRotation()
    })

    return {
      displayStates,
      currentLoadingMessage,
      allStatesComplete,
      mainHeading,
      mainMessage,
      error,
      canViewLive,
      handleClose,
      handleContinue,
      handleViewLive,
      handleOpenBilling,
    }
  }
})
</script>

<style scoped>
.modal-overlay {
  animation: fadeIn 0.3s ease;
}

.modal-container {
  width: 100%;
  max-width: min(34rem, 100%);
}

.modal-body {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem 1rem 0.9rem;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.header-section {
  text-align: center;
  margin-bottom: 0.35rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.6rem;
}

.spinner-wrap {
  display: flex;
  justify-content: center;
  align-items: center;
}

/* Same spinner language as LoadingOverlay — ring + pulsing core.
   Words now live BELOW the spinner instead of inside the ring. */
.spinner-container {
  position: relative;
  width: 4.4rem;
  height: 4.4rem;
}

.spinner-ring {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 4px solid transparent;
  border-top-color: var(--ui-inverse-text);
  border-right-color: var(--ui-inverse-text);
  border-radius: 50%;
  animation: spin 1.5s cubic-bezier(0.68, -0.55, 0.27, 1.55) infinite;
}

.spinner-core {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 40%;
  height: 40%;
  background: var(--ui-inverse-cta-bg);
  border-radius: 50%;
  animation: pulse 1.5s ease-in-out infinite;
}

.spinner-container.completed .spinner-ring {
  animation: none;
  border-color: rgba(var(--vt-c-accent-rgb), 0.85);
}

.spinner-container.completed .spinner-core {
  animation: none;
  width: 48%;
  height: 48%;
  background: var(--ui-inverse-cta-bg);
  box-shadow: 0 0 20px rgba(var(--vt-c-accent-rgb), 0.45);
}

.spinner-badge {
  position: absolute;
  top: -0.45rem;
  left: 50%;
  transform: translateX(-50%);
  background: var(--ui-inverse-cta-bg);
  color: var(--ui-inverse-cta-text);
  font-size: 0.7rem;
  font-weight: 800;
  border-radius: 999px;
  padding: 0.14rem 0.55rem;
  letter-spacing: 0.05em;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes pulse {
  0% { transform: translate(-50%, -50%) scale(0.8); opacity: 0.5; }
  50% { transform: translate(-50%, -50%) scale(1.2); opacity: 1; box-shadow: 0 0 20px rgba(16, 26, 52, 0.35); }
  100% { transform: translate(-50%, -50%) scale(0.8); opacity: 0.5; }
}

.loading-word {
  margin: 0;
  color: var(--ui-inverse-text);
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: lowercase;
  font-family: var(--ui-inverse-font-family);
  animation: wordFade 2s ease-in-out infinite;
}

@keyframes wordFade {
  0%, 100% { opacity: 0.65; }
  50% { opacity: 1; }
}

.main-message {
  margin: 0;
  color: var(--ui-inverse-text-muted);
  font-size: 0.96rem;
  line-height: 1.42;
  font-family: var(--ui-inverse-font-family);
  max-width: 36ch;
  margin-inline: auto;
}

.states-container {
  display: flex;
  flex-direction: column;
  gap: 0.36rem;
  margin: 0;
  padding-right: 0.1rem;
  max-height: min(40vh, 16rem);
  overflow-y: auto;
}

.state-item {
  position: relative;
  display: grid;
  grid-template-columns: 1.5rem minmax(0, 1fr);
  align-items: start;
  column-gap: 0.65rem;
  row-gap: 0.1rem;
  padding: 0.58rem 0.62rem 0.56rem;
  background: rgba(16, 26, 52, 0.07);
  border-radius: 0.72rem;
  border: 1px solid rgba(16, 26, 52, 0.14);
  transition: background 0.25s ease, border-color 0.25s ease;
}

.state-item.running {
  background: rgba(16, 26, 52, 0.1);
  border-color: rgba(16, 26, 52, 0.3);
}

.state-item.completed {
  background: rgba(16, 26, 52, 0.1);
  border-color: rgba(16, 26, 52, 0.28);
}

.state-item.failed {
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(143, 20, 52, 0.35);
}

.state-indicator {
  min-width: 1.35rem;
  height: 1.35rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 0.05rem;
}

.check-icon {
  width: 1.35rem;
  height: 1.35rem;
  background: var(--ui-inverse-cta-bg);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--ui-inverse-cta-text);
  font-weight: bold;
  font-size: 0.78rem;
  animation: checkPop 0.4s ease;
}

@keyframes checkPop {
  0% { transform: scale(0); }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); }
}

.error-icon {
  width: 1.35rem;
  height: 1.35rem;
  background: #ef4444;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 0.78rem;
}

.running-spinner {
  width: 1.35rem;
  height: 1.35rem;
  border: 0.1875rem solid rgba(16, 26, 52, 0.2); /* 3px */
  border-top-color: var(--ui-inverse-text);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.pending-dot {
  width: 1.35rem;
  height: 1.35rem;
  border: 0.125rem solid rgba(16, 26, 52, 0.22); /* 2px */
  border-radius: 50%;
  background: rgba(16, 26, 52, 0.08);
}

.state-content {
  flex: 1;
}

.state-name {
  color: var(--ui-inverse-text);
  font-size: 0.86rem;
  font-weight: 700;
  margin-bottom: 0.1rem;
  font-family: var(--ui-inverse-font-family);
  line-height: 1.2;
}

.state-description {
  color: var(--ui-inverse-text-muted);
  font-size: 0.72rem;
  font-family: var(--ui-inverse-font-family);
  line-height: 1.2;
}

.state-connector {
  position: absolute;
  left: 1.38rem;
  top: 1.95rem;
  width: 0.11rem;
  height: calc(100% - 1.22rem);
  background: rgba(16, 26, 52, 0.15);
}

.error-message {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.9375rem;
  background: rgba(143, 20, 52, 0.08);
  border: 0.0625rem solid rgba(143, 20, 52, 0.3);
  border-radius: 0.5rem;
  color: #8f1434;
  margin-top: 1.25rem;
  font-family: var(--ui-inverse-font-family);
}

.modal-footer {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.55rem;
}

.cta-button {
  width: 100%;
  padding: 0.78rem 0.6rem;
  font-size: 0.92rem;
  font-weight: 700;
  color: var(--vt-c-accent);
  background: var(--vt-c-background-primary);
  border: 1px solid var(--vt-c-accent);
  border-radius: 999px;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
  box-shadow: none;
  font-family: var(--ui-inverse-font-family);
  white-space: nowrap;
}

.cta-button:hover,
.cta-button:focus-visible {
  background: rgba(var(--vt-c-accent-rgb), 0.12);
}

.cta-button--primary {
  background: var(--vt-c-accent);
  color: var(--vt-c-background-primary);
}

.cta-button--primary:hover,
.cta-button--primary:focus-visible {
  background: var(--ui-inverse-cta-bg-hover);
  color: var(--vt-c-background-primary);
}

.cta-button:active {
  transform: translateY(0);
}

.cta-button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

@media (max-width: 48rem) {
  .states-container {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.38rem;
    max-height: min(39vh, 12.25rem);
    padding-right: 0;
  }

  .state-item {
    grid-template-columns: 1.15rem minmax(0, 1fr);
    align-items: center;
    padding: 0.46rem 0.5rem;
    column-gap: 0.42rem;
    row-gap: 0;
    border-radius: 0.58rem;
  }

  .state-indicator {
    min-width: 1.05rem;
    height: 1.05rem;
    margin-top: 0;
  }

  .check-icon,
  .error-icon,
  .running-spinner,
  .pending-dot {
    width: 1.05rem;
    height: 1.05rem;
  }

  .state-connector {
    display: none;
  }

  .state-name {
    font-size: 0.72rem;
    margin-bottom: 0;
    line-height: 1.15;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .state-description {
    display: none;
  }
}

@media (max-width: 37.5rem) {
  .modal-container {
    max-width: min(32rem, 100%);
  }

  .modal-body {
    padding: 0.8rem 0.8rem 0.75rem;
    gap: 0.75rem;
  }

  .spinner-container {
    width: 3.6rem;
    height: 3.6rem;
  }

  .main-message {
    font-size: 0.9rem;
    line-height: 1.35;
  }

  .loading-word {
    font-size: 0.85rem;
  }

  .states-container {
    max-height: min(38vh, 11.5rem);
  }

  .modal-footer {
    gap: 0.42rem;
  }

  .cta-button {
    padding: 0.7rem 0.4rem;
    font-size: 0.82rem;
  }
}

@media (max-height: 760px) {
  .modal-body {
    padding: 0.72rem 0.72rem 0.68rem;
    gap: 0.66rem;
  }

  .header-section {
    margin-bottom: 0.2rem;
    gap: 0.42rem;
  }

  .spinner-container {
    width: 3.3rem;
    height: 3.3rem;
  }

  .main-message {
    font-size: 0.84rem;
    line-height: 1.3;
  }

  .loading-word {
    font-size: 0.8rem;
  }

  .states-container {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.38rem;
    max-height: min(33vh, 9.5rem);
    padding-right: 0;
  }

  .state-item {
    grid-template-columns: 1.15rem minmax(0, 1fr);
    align-items: center;
    padding: 0.45rem 0.5rem;
    column-gap: 0.42rem;
    border-radius: 0.58rem;
    row-gap: 0;
  }

  .state-indicator {
    min-width: 1.05rem;
    height: 1.05rem;
    margin-top: 0;
  }

  .check-icon,
  .error-icon,
  .running-spinner,
  .pending-dot {
    width: 1.05rem;
    height: 1.05rem;
  }

  .state-connector {
    display: none;
  }

  .state-name {
    font-size: 0.72rem;
    line-height: 1.15;
    margin-bottom: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .state-description {
    display: none;
  }

  .modal-footer {
    padding: 0.7rem 0.85rem;
  }

  .cta-button {
    padding: 0.64rem 0.4rem;
    font-size: 0.78rem;
  }
}

@media (max-height: 760px) and (max-width: 420px) {
  .states-container {
    grid-template-columns: 1fr;
    max-height: min(34vh, 10.25rem);
  }
}

@media (max-width: 48rem) and (max-height: 860px) {
  .states-container {
    max-height: min(33vh, 10rem);
  }
}
</style>
